/**
 * Tee Agent Zustand Store
 * 
 * Manages state for the Tee Agent AI assistant
 */
import { create } from "zustand";
import type {
  ChatMessage,
  ChatSession,
  AgentAction,
  AgentCapability,
  ScreenContext,
} from "@/types/teeAgent";
import type { ResponseOption } from "@/components/TeeAgent/ResponseOptionCard";
import type { AgentOrbState } from "@/components/TeeAgent/AgentOrb";
import type { TaskStep } from "@/components/TeeAgent/TaskTimeline";

export type CopilotLayoutMode = "panel" | "full";

interface TeeAgentState {
  // UI State
  isOpen: boolean;
  isLoading: boolean;
  isMinimized: boolean;
  isPinned: boolean;

  // Layout: right-side overlay panel vs full-screen workspace
  layoutMode: CopilotLayoutMode;
  // Whether the history list is shown in place of the active conversation
  showHistory: boolean;

  // Agent State
  agentState: AgentOrbState;
  
  // Voice State
  isVoiceMode: boolean;
  
  // Task Timeline State
  taskSteps: TaskStep[];
  
  // Screen Context
  screenContext: ScreenContext | null;
  
  // Chat State
  currentSession: ChatSession | null;
  messages: ChatMessage[];
  sessions: ChatSession[];
  
  // Action State
  pendingAction: AgentAction | null;
  
  // Response Options State (for AI response choices)
  responseOptions: ResponseOption[];
  
  // Capabilities
  capabilities: AgentCapability[];
  isCapable: boolean; // Whether user has any capabilities
  
  // Input State
  inputMessage: string;
  
  // Actions
  setIsOpen: (isOpen: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  setIsMinimized: (isMinimized: boolean) => void;
  setIsPinned: (isPinned: boolean) => void;

  setLayoutMode: (mode: CopilotLayoutMode) => void;
  toggleLayoutMode: () => void;
  setShowHistory: (showHistory: boolean) => void;

  setAgentState: (state: AgentOrbState) => void;
  setIsVoiceMode: (isVoice: boolean) => void;
  setTaskSteps: (steps: TaskStep[]) => void;
  setScreenContext: (ctx: ScreenContext | null) => void;
  
  setCurrentSession: (session: ChatSession | null) => void;
  setMessages: (messages: ChatMessage[]) => void;
  addMessage: (message: ChatMessage) => void;
  setSessions: (sessions: ChatSession[]) => void;
  
  setPendingAction: (action: AgentAction | null) => void;
  
  // Response Options Actions
  setResponseOptions: (options: ResponseOption[]) => void;
  clearResponseOptions: () => void;
  
  setCapabilities: (capabilities: AgentCapability[]) => void;
  
  setInputMessage: (message: string) => void;
  
  // Utility Actions
  toggleOpen: () => void;
  toggleMinimized: () => void;
  togglePinned: () => void;
  clearChat: () => void;
  reset: () => void;
}

export const useTeeAgentStore = create<TeeAgentState>((set) => ({
  // Initial State
  isOpen: false,
  isLoading: false,
  isMinimized: false,
  isPinned: false,

  layoutMode: "panel",
  showHistory: false,

  agentState: "idle",
  isVoiceMode: false,
  taskSteps: [],
  screenContext: null,
  
  currentSession: null,
  messages: [],
  sessions: [],
  
  pendingAction: null,
  
  responseOptions: [],
  
  capabilities: [],
  isCapable: false,
  
  inputMessage: "",
  
  // Actions
  setIsOpen: (isOpen) => set({ isOpen }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setIsMinimized: (isMinimized) => set({ isMinimized }),
  setIsPinned: (isPinned) => set({ isPinned }),

  setLayoutMode: (layoutMode) => set({ layoutMode }),
  toggleLayoutMode: () =>
    set((state) => ({ layoutMode: state.layoutMode === "panel" ? "full" : "panel" })),
  setShowHistory: (showHistory) => set({ showHistory }),

  setAgentState: (agentState) => set({ agentState }),
  setIsVoiceMode: (isVoiceMode) => set({ isVoiceMode }),
  setTaskSteps: (taskSteps) => set({ taskSteps }),
  setScreenContext: (screenContext) => set({ screenContext }),
  
  setCurrentSession: (currentSession) => set({ currentSession }),
  setMessages: (messages) => set({ messages }),
  addMessage: (message) => set((state) => ({ 
    messages: [...state.messages, message] 
  })),
  setSessions: (sessions) => set({ sessions }),
  
  setPendingAction: (pendingAction) => set({ pendingAction }),
  
  // Response Options Actions
  setResponseOptions: (responseOptions) => set({ responseOptions }),
  clearResponseOptions: () => set({ responseOptions: [] }),
  
  setCapabilities: (capabilities) => set({ 
    capabilities,
    isCapable: capabilities.some(c => c.available)
  }),
  
  setInputMessage: (inputMessage) => set({ inputMessage }),
  
  // Utility Actions
  toggleOpen: () => set((state) => ({ isOpen: !state.isOpen })),
  toggleMinimized: () => set((state) => ({ isMinimized: !state.isMinimized })),
  togglePinned: () => set((state) => ({ isPinned: !state.isPinned })),
  
  clearChat: () => set({
    messages: [],
    currentSession: null,
    pendingAction: null,
    responseOptions: [],
    taskSteps: [],
    inputMessage: "",
  }),
  
  reset: () => set({
    isOpen: false,
    isLoading: false,
    isMinimized: false,
    isPinned: false,
    layoutMode: "panel",
    showHistory: false,
    agentState: "idle",
    isVoiceMode: false,
    taskSteps: [],
    screenContext: null,
    currentSession: null,
    messages: [],
    sessions: [],
    pendingAction: null,
    responseOptions: [],
    capabilities: [],
    isCapable: false,
    inputMessage: "",
  }),
}));

